import { yupResolver } from '@hookform/resolvers/yup';
import { isEmpty } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Form,
  FormGroup,
  Label,
} from 'reactstrap';

import { toast } from 'react-toastify';

import { getPropertiesAccessDetails } from '../../services/services';
import CustomRadioButton from '../../shared/form-field/custom-radio-button';
import InputField from '../../shared/form-field/input-field';
import Loader from '../../shared/loader';
import catchHandler from '../../../helpers/catchHandler';

import LoaderRound from '../../shared/loader-round';
import useSubmitCreateWO from '../../shared/hooks/useSubmitCreateWO';
import StepCounter from '../../shared/steps-counter';

const AddPropertyAccessDetails = () => {
  const [propertyAccessOptions, setPropertyAccessOptions] = useState({});
  const [isPropertyAccessDetailsFetching, setIsPropertyAccessDetailsFetching] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const tenantEntryDetails = useSelector((state) => state.tenantJobEntry);

  const schema = yup.object().shape({
    propertyAccess: yup.string().trim().required('select a property access option'),
    specificAccessNote: yup.string().when('propertyAccess', {
      is: (value) => ['hidden-key', 'digital-key-code'].includes(value),
      then: yup.string().trim().required('This is a required field'),
    }),
  });

  const buildFormData = useCallback((data) => {
    const formData = {};
    formData.propertyAccess = data?.property_access || Object.keys(propertyAccessOptions)[0];
    formData.specificAccessNote = data?.specific_access_note || '';
    formData.doorCode = data?.door_code || '';
    return formData;
  }, [propertyAccessOptions]);

  const methods = useForm({
    defaultValues: {
      propertyAccess: '',
      doorCode: '',
      specificAccessNote: '',
    },
    resolver: yupResolver(schema),
  });

  const {
    handleSubmit,
    setError,
    reset,
    clearErrors,
    formState: { errors },
  } = methods;
  const { onSubmit, isFormSubmitting } = useSubmitCreateWO({
    errorCallback: (err) => {
      catchHandler(err, setError);
    },
  });

  useEffect(() => {
    if (!isEmpty(tenantEntryDetails)) {
      reset(buildFormData(tenantEntryDetails));
    }
  }, [buildFormData, reset, propertyAccessOptions, tenantEntryDetails]);

  useEffect(() => {
    if (!isEmpty(errors?.general_errors?.message?.messages)) {
      errors?.general_errors?.message?.messages?.forEach((error) => {
        toast(error, { type: 'error' });
        clearErrors();
      });
    }
    if (!isEmpty(errors?.general_errors?.message?.dates)) {
      document
        ?.getElementById(
          `schedule-date-${errors?.general_errors?.message?.dates[0]}`,
        )
        .scrollIntoView({
          behavior: 'smooth',
          block: 'end',
          inline: 'nearest',
        });
    }
  }, [
    errors?.general_errors?.message?.messages,
    errors?.general_errors?.message?.dates,
    clearErrors,
  ]);

  useEffect(() => {
    setIsPropertyAccessDetailsFetching(true);
    getPropertiesAccessDetails().then((res) => {
      setIsPropertyAccessDetailsFetching(false);
      setPropertyAccessOptions(res?.how_to_access_property);
    }).catch(() => {
      setIsPropertyAccessDetailsFetching(false);
    });
  }, [setError, dispatch]);

  return (
    <FormProvider {...methods}>
      <Form className="info-form" onSubmit={handleSubmit(onSubmit)}>
        <StepCounter currentStep={5} />
        <h2>Property Access Details</h2>
        <p>Please confirm access information.</p>
        {isPropertyAccessDetailsFetching ? (
          <Loader />
        ) : (
          <div className="property-access-details-form">
            {Object.keys(propertyAccessOptions)?.map((item) => {
              const data = propertyAccessOptions[item];
              return (
                <React.Fragment key={item}>
                  <FormGroup check>
                    <CustomRadioButton
                      name="propertyAccess"
                      type="radio"
                      value={item}
                      clearError
                      fieldValue={item}
                      hideFormError
                    />
                    {' '}
                    <Label check>
                      <div className="circle">
                        <div className="circle-selected" />
                      </div>
                      <p>{data}</p>
                    </Label>
                  </FormGroup>
                </React.Fragment>
              );
            })}
            <FormGroup className="specific-access-wrapper">
              <Label>
                Specific Access Note
              </Label>
              <InputField
                name="specificAccessNote"
                type="textarea"
                clearError
                className="specific-access-note"
                placeholder="Enter any access notes here"
              />
            </FormGroup>
          </div>
        )}
        <div className="footer-action">
          <div className="container d-flex gap-3">
            <Button
              type="button"
              color="secondary"
              block
              disabled={isFormSubmitting}
              onClick={() => navigate('/availability')}
            >
              Back
            </Button>
            <Button
              type="submit"
              color="primary"
              block
              disabled={isFormSubmitting}
            >
              {isFormSubmitting ? <LoaderRound /> : 'Submit Request'}
            </Button>
          </div>
        </div>
      </Form>
    </FormProvider>
  );
};

export default AddPropertyAccessDetails;
